import { useEffect, useState } from 'react';
import { Box, Card, Tab, Tabs, styled } from '@mui/material';
import AccountSettings from '../../components/Settings/Settings';
import BarcodeSettings from '../../components/BarcodeSettings/BarcodeSettings';
import TagsMaster from '../../components/TagsMaster/TagsMaster';
import { TabPanel } from '../../components/Dialog/TabPanel';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchAccountById } from '../../store/accountThunk';

interface StyledTabsProps {
  children?: React.ReactNode;
  value: number;
  onChange: (event: React.SyntheticEvent, newValue: number) => void;
}

const StyledTabs = styled((props: StyledTabsProps) => (
  <Tabs
    {...props}
  />
))({
  backgroundColor: 'transparent',
  padding: '0px',
  minHeight: '40px',
  maxWidth: 'fit-content',
  '& .MuiTabs-flexContainer': {
    gap: '4px',
  },
  '& .MuiTabs-indicator': {
    display: 'none',
  },
  '& .MuiTab-root': {
    textTransform: 'none',
    fontSize: '14px',
    fontWeight: 500,
    color: '#4b5563',
    zIndex: 2,
    minWidth: 'auto',
    height: '36px',
    minHeight: '36px',
    padding: '8px 20px',
    borderRadius: '8px',
    transition: 'all 0.2s ease-in-out',
    '&:hover:not(.Mui-selected)': {
      backgroundColor: '#f3f4f6',
      color: '#047857',
    },
    '&.Mui-selected': {
      backgroundColor: '#059669',
      color: '#ffffff',
      fontWeight: 600,
      boxShadow: '0 2px 8px rgba(5, 150, 105, 0.3)',
    },
  },
});

interface TabPanelProps {
  index: number;
  value: number;
}

const StyledTabPanel = styled((props: TabPanelProps) => (
  <TabPanel
    {...props}
  />
))({
  // Only reset TabPanel's own wrapper; a descendant selector would also strip padding from boxes inside the cards
  '& > .MuiBox-root': {
    backgroundColor: 'transparent !important',
    padding: '0px !important',
    margin: '0 !important',
    minHeight: 'auto !important',
  }
});

interface StyledTabProps {
  label: string;
}

const StyledTab = styled((props: StyledTabProps) => (
  <Tab disableRipple {...props} />
))(({ theme }) => ({
  textTransform: 'none',
  fontWeight: theme.typography.fontWeightRegular,
  fontSize: theme.typography.pxToRem(15),
  marginRight: theme.spacing(1),
  color: '#545454',
  '&.Mui-selected': {
    color: '#059669',
  },
  '&.Mui-focusVisible': {
    backgroundColor: 'rgba(100, 95, 228, 0.32)',
  },
}));

/**
 * Standalone Settings page reached from the header gear icon.
 * Hosts the account settings module that previously lived in the
 * "Settings" tab of My Business Information, one section per tab.
 */
const Settings = () => {
  const dispatch = useAppDispatch();

  const [value, setValue] = useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const { selectedAccount } = useAppSelector(state => state.auth);
  const accountDetails = useAppSelector(state => state.account.accountDetails);
  const crmEnabled = !!selectedAccount?.unofyCrm?.enabled;

  // The settings cards read the account from the store, so make sure it is loaded
  // for the currently selected business (this page has no :accountId param).
  useEffect(() => {
    if (selectedAccount?._id && accountDetails?._id !== selectedAccount._id) {
      dispatch(fetchAccountById(selectedAccount._id));
    }
  }, [selectedAccount?._id, accountDetails?._id, dispatch]);

  return (
    <Card sx={{ mt: 1, mb: 3, mr: 2, borderRadius: '8px', boxShadow: 'none', border: '1px solid #dcdcdc', bgcolor: '#fff' }}>
      <Box sx={{ px: 3, py: 0.75, bgcolor: '#fff', borderBottom: '1px solid #dcdcdc', overflowX: 'auto' }}>
        <StyledTabs value={value}
          onChange={handleChange}
          aria-label="settings tabs">
          <StyledTab label="Notifications" />
          <StyledTab label="Default Cash Account" />
          <StyledTab label="Barcode Format" />
          <StyledTab label="Tags" />
          {/* Tabs takes its children as an array — a false here would break the indices,
              so the CRM tab is only appended when the integration is on. */}
          {crmEnabled ? <StyledTab label="CRM" /> : undefined}
        </StyledTabs>
      </Box>

      <Box sx={{
        px: 3, py: 2.5, bgcolor: '#fff',
        '& .c-card2': { bgcolor: '#fff', border: '1px solid #dcdcdc', borderRadius: '8px', boxShadow: 'none', cursor: 'default' },
        // Invoice.css turns any hovered element's border blue inside .c-card2
        '& .c-card2 :hover:not(.MuiButton-root):not(.MuiInputBase-root)': { borderColor: '#dcdcdc' },
        // The shared #9da7b7 helper text is too faint to read comfortably
        '& .c-card2 [style*="color: rgb(157, 167, 183)"]': { color: '#6b7280 !important' },
      }}>
        <StyledTabPanel value={value} index={0} >
          <AccountSettings section="notifications" />
        </StyledTabPanel>
        <StyledTabPanel value={value} index={1} >
          <AccountSettings section="cashSale" />
        </StyledTabPanel>
        <StyledTabPanel value={value} index={2} >
          <BarcodeSettings />
        </StyledTabPanel>
        <StyledTabPanel value={value} index={3} >
          <TagsMaster />
        </StyledTabPanel>
        {crmEnabled && <StyledTabPanel value={value} index={4} >
          <AccountSettings section="crm" />
        </StyledTabPanel>}
      </Box>
    </Card>
  );
};

export default Settings;
